import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { FlexCol } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Multiselect } from '@/components/ui/multiselect';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateSharedKnowledgeGroupService,
  useSharedKnowledgeGroupsService,
  useUpdateSharedKnowledgeGroupService,
} from '@/services/sharedFiles';
import { REGION_DISPLAY_NAMES, SupportedRegion } from '@ditto/shared';
import { cn } from '@/utils/classUtils';
import { ChevronDown, Pencil, Plus } from 'lucide-react';
import { useEffect, useState, type ChangeEvent, type FC } from 'react';
import { toast } from 'sonner';

type CreateGroupModalProps = {
  mode: 'create';
};

type EditGroupModalProps = {
  mode: 'edit';
  id: string;
  name: string;
  uname: string;
  description: string;
  regions: SupportedRegion[];
};

export const CreateEditGroupModal: FC<
  CreateGroupModalProps | EditGroupModalProps
> = props => {
  const isEdit = props.mode === 'edit';
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(isEdit ? props.name : '');
  const [uname, setUname] = useState(isEdit ? props.uname : '');
  const [description, setDescription] = useState(
    isEdit ? props.description : ''
  );
  const [regions, setRegions] = useState<SupportedRegion[]>(
    isEdit ? props.regions : []
  );

  const regionOptions = [
    { label: REGION_DISPLAY_NAMES.US, value: 'US' },
    { label: REGION_DISPLAY_NAMES.EU, value: 'EU' },
    { label: REGION_DISPLAY_NAMES.WORLD, value: 'WORLD' },
  ];

  const { groups, isPending } = useSharedKnowledgeGroupsService();
  const { createGroup } = useCreateSharedKnowledgeGroupService();
  const { updateGroup } = useUpdateSharedKnowledgeGroupService();

  const blacklistUnames = isEdit
    ? groups.filter(group => group.id !== props.id).map(group => group.uname)
    : groups.map(group => group.uname);

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };
  const handleUnameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUname(e.target.value);
  };
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  useEffect(() => {
    if (isOpen) {
      if (isEdit && props.mode === 'edit') {
        setName(props.name);
        setUname(props.uname);
        setDescription(props.description);
        setRegions(props.regions);
      } else {
        setName('');
        setUname('');
        setDescription('');
        setRegions([]);
      }
    }
  }, [isOpen, isEdit, props]);

  const validateInput = (): { success: boolean; errorMessage: string } => {
    if (!name.trim()) {
      return { success: false, errorMessage: 'Name is required' };
    }
    if (!uname.trim()) {
      return { success: false, errorMessage: 'Uname is required' };
    }
    //Check if uname contains spaces
    if (uname.includes(' ')) {
      return { success: false, errorMessage: 'Uname must not contain spaces' };
    }
    //Check if uname contains only lowercase letters, dashes and numbers
    if (!/^[a-z0-9-]+$/.test(uname)) {
      return {
        success: false,
        errorMessage:
          'Uname must only contain lowercase letters, numbers, and dashes',
      };
    }
    if (!description.trim()) {
      return { success: false, errorMessage: 'Description is required' };
    }
    if (blacklistUnames.includes(uname)) {
      return { success: false, errorMessage: 'Uname is already taken' };
    }
    return { success: true, errorMessage: '' };
  };

  const resetInput = () => {
    setName('');
    setUname('');
    setDescription('');
    setRegions([]);
  };

  const handleSubmit = () => {
    const { success, errorMessage } = validateInput();
    if (success) {
      if (props.mode === 'create') {
        createGroup({
          name,
          uname,
          description,
          regions,
        });
        resetInput();
      }
      if (props.mode === 'edit') {
        updateGroup({
          id: props.id,
          group: {
            name,
            uname,
            description,
            regions,
          },
        });
      }
      setIsOpen(false);
      return;
    }
    toast.error(errorMessage);
  };

  if (isPending) {
    return <div>Loading...</div>;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon">
            <Pencil />
          </Button>
        ) : (
          <Button variant="outline" className="w-full">
            <Plus />
            Add New Knowledge Group
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Knowledge Group' : 'Create Knowledge Group'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Edit the knowledge group for shared knowledge.'
              : 'Create a new knowledge group for shared knowledge.'}
          </DialogDescription>
        </DialogHeader>
        <FlexCol gap="4">
          <FlexCol gap="2">
            <Label>Name</Label>
            <Input value={name} onChange={handleNameChange} />
          </FlexCol>
          <FlexCol gap="2">
            <Label>Uname</Label>
            <Input value={uname} onChange={handleUnameChange} />
          </FlexCol>
          <FlexCol gap="2">
            <Label>Description</Label>
            <Textarea
              rows={4}
              value={description}
              onChange={handleDescriptionChange}
            />
          </FlexCol>
          <FlexCol gap="2">
            <Label>Regions</Label>
            <Multiselect
              options={regionOptions}
              selected={regions}
              onChange={selected => setRegions(selected as SupportedRegion[])}
              placeholder="Select regions..."
              showCount={true}
              showClearAll={true}
              trigger={
                <Button
                  variant="outline"
                  role="combobox"
                  className={cn(
                    'justify-between font-normal w-full',
                    regions.length === 0 && 'text-muted-foreground'
                  )}
                >
                  <span className="truncate">
                    {regions.length === 0
                      ? 'Select regions...'
                      : regions.length === 1
                        ? regionOptions.find(opt => opt.value === regions[0])
                            ?.label || regions[0]
                        : `${regions.length} selected`}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              }
            />
          </FlexCol>
        </FlexCol>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit}>{isEdit ? 'Edit' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
